import { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useShowInvoicing } from '@/hooks/use-show-invoicing';
import { useDailyReport } from '@/hooks/useDailyReports';
import { ProjectsDashboardPage } from './projects-dashboard-page';
import { ProjectsListPage } from './project-list/projects-list-page';
import { ProjectCreatePage } from './project-list/project-create-page';
import { ProjectDetailPage } from './project-detail/project-detail-page';
import { JobJSAsPage } from '../jobs/job-jsas-page';
import { TimeEntryPage } from './time/time-entry-page';
import { BidsPage } from './bids/bids-page';
import { CreateBidPage } from './bids/create-bid-page';
import { BidDetailPage } from './bids/bid-detail-page';
import { InvoicesPage } from './invoices/invoices-page';
import { CreateInvoicePage } from './invoices/create-invoice-page';
import { InvoiceDetailPage } from './invoices/invoice-detail-page';

function RedirectClientDetail() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/clients/${id}`} replace />;
}

function RedirectProjectFolder() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/projects/${id}`} replace />;
}

function RedirectDailyReports() {
  const { id, '*': rest } = useParams<{ id: string; '*': string }>();
  const suffix = rest ? `/${rest}` : '';
  return <Navigate to={`/work/daily-reports/${id}${suffix}`} replace />;
}

export function ResolveOldDailyReportRedirect() {
  const { id, '*': rest } = useParams<{ id: string; '*': string }>();
  const reportId = id ? Number(id) : 0;
  const { data, isLoading, error } = useDailyReport(reportId);
  const navigate = useNavigate();

  useEffect(() => {
    if (data) {
      const suffix = rest ? `/${rest}` : '';
      navigate(`/work/daily-reports/${data.project}/${id}${suffix}`, { replace: true });
    } else if (!isLoading && (error || !data)) {
      navigate('/work/daily-reports', { replace: true });
    }
  }, [data, isLoading, error, id, navigate, rest]);

  return null;
}

export function ProjectsPage() {
  const showInvoicing = useShowInvoicing();

  return (
    <Routes>
      <Route index element={<ProjectsDashboardPage />} />
      {/* Redirect legacy /projects/clients/* to shared /clients/* */}
      <Route path="clients" element={<Navigate to="/clients" replace />} />
      <Route path="clients/new" element={<Navigate to="/clients/new" replace />} />
      <Route path="clients/:id" element={<RedirectClientDetail />} />
      <Route path="list" element={<ProjectsListPage />} />
      <Route path="new" element={<ProjectCreatePage />} />
      <Route path="time" element={<TimeEntryPage />} />
      {showInvoicing && <Route path="bids" element={<BidsPage />} />}
      {showInvoicing && <Route path="bids/new" element={<CreateBidPage />} />}
      {showInvoicing && <Route path="bids/:id/edit" element={<CreateBidPage />} />}
      {showInvoicing && <Route path="bids/:id" element={<BidDetailPage />} />}
      {showInvoicing && <Route path="invoices" element={<InvoicesPage />} />}
      {showInvoicing && <Route path="invoices/new" element={<CreateInvoicePage />} />}
      {showInvoicing && <Route path="invoices/:id" element={<InvoiceDetailPage />} />}
      <Route path=":id/daily-reports/*" element={<RedirectDailyReports />} />
      <Route path=":id/jsas" element={<JobJSAsPage />} />
      <Route path=":id/folder" element={<RedirectProjectFolder />} />
      <Route path=":id" element={<ProjectDetailPage />} />
    </Routes>
  );
}
